import React, { useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useParams } from "react-router-dom";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";

import { cancelWorkOrderByClient } from "../../../services/api/workOrder";
import BaseSelect from "../../../components/ui/BaseSelect";
import dayjs from "dayjs";
import { CLIENT_CANCELLATION_REASONS } from "../../../utils/enum";

const CancelByClientModal = ({
  cancelByClientOpen,
  setCancelByClientOpen,
  refetchUserAllowedAction,
}) => {
  const dispatch = useDispatch();
  const [cancellationReason, setCancellationReason] = useState("");
  const [reason, setReason] = useState("");
  const params = useParams();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const {
    isPending: isCancelWorkOrderByClientLoading,
    mutateAsync: cancelWorkOrderByClientMutation,
  } = useMutation({
    mutationFn: cancelWorkOrderByClient,
    enabled: false,
  });

  const handleCancelWorkOrder = async () => {
    try {
      const payload = {
        cancellationReason,
        reason: cancellationReason === "OTHER" ? reason : undefined,
        id: params?.id,
      };

      await cancelWorkOrderByClientMutation(payload);
      updateWorkOrder();
      refetchUserAllowedAction();
      setCancelByClientOpen(false);
      setCancellationReason("");
      setReason("");
      dispatch(
        showToast({
          message: "Work Order Cancelled",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };

  const isSubmitDisabled =
    isCancelWorkOrderByClientLoading ||
    !cancellationReason ||
    (cancellationReason === "OTHER" && !reason.trim());

  return (
    <BaseModal
      open={cancelByClientOpen}
      close={() => setCancelByClientOpen(false)}
      style={{ width: "700px" }}
    >
      <div className="container">
        <h6 className="text-dark border-bottom mb-4 pb-1">Cancel Work Order</h6>
        <p className="bg-primary px-3 py-2 text-white rounded">
          This work order is being cancelled by the client. The engineer will be notified.
        </p>

        <div className="flex items-center justify-between mt-4">
          <p className="font-normal">Cancelled at</p>
          <p>
            {dayjs().format("dddd, MM/D/YYYY")} at {dayjs().format("h:mm A")} (
            {Intl.DateTimeFormat().resolvedOptions().timeZone})
          </p>
        </div>

        <p className="font-normal mt-8">Reason For Cancellation</p>
        <div className="mt-1">
          <BaseSelect
            labelShrink={false}
            fullHeight={false}
            className={"h-10"}
            placeholder="Select a reason"
            isDefaultField={false}
            required={true}
            options={CLIENT_CANCELLATION_REASONS}
            value={cancellationReason}
            onChange={(e) => {
              setCancellationReason(e.target.value);
              setReason("");
            }}
          />
        </div>

        {cancellationReason === "OTHER" && (
          <>
            <p className="font-normal mt-4">Please describe the reason</p>
            <textarea
              value={reason}
              className="bg-zinc-100 border-t border-zinc-100 w-full rounded-md h-28 resize-none p-4 mt-1 focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300]"
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter cancellation reason..."
            />
          </>
        )}
      </div>
      <div
        className="flex items-center justify-center bg-white py-6 px-5 mt-3"
        style={{
          boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="py-[5px] px-2 bg-[#E8E8E8] text-primary"
          fullWidth={false}
          onClick={() => setCancelByClientOpen(false)}
        >
          Cancel
        </BaseButton>
        <BaseButton
          disabled={isSubmitDisabled}
          isLoading={isCancelWorkOrderByClientLoading}
          loaderSize={18}
          className="py-[5px] px-4 ml-4 min-w-[100px]"
          color="primary"
          fullWidth={false}
          onClick={handleCancelWorkOrder}
        >
          Confirm
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default CancelByClientModal;
